/**
 * CreateAssignmentModal — step-by-step homework creation.
 *
 * Flows per homework type:
 *   Kitap Ödevi         type → class → students → book → details → verify
 *   Test Ödevi          type → class → students → book → konu (category) → test (section) → details → verify
 *   Haftalık Tarama     type → class → students → book → konu → test → details → verify
 *   Diğer Ödevler       type → diger (title + class + desc + file upload) → students → verify
 *
 * Kitap Ödevi is PDF-reading only — NO test selection, student just gets the book PDF.
 * Test/Haftalık require selecting a specific test (e.g. "Kazanım Denemesi 5").
 */
import React, { useEffect, useRef, useState } from 'react';
import { teacherApi } from '../services/api';
import OutcomePicker, { OutcomeOptions } from './OutcomePicker';

const F = { fontFamily: 'Inter' } as const;

interface ClassData { id: string; name: string; grade_number?: number | null; students: { id: string; uuid: string; name: string }[] }
interface BookData { id: number; title: string; cover_image_url: string | null; cover_url?: string | null; subject?: { id: number; name: string } | null; grade_levels?: { grade_number: number | null; name: string } | null }
interface SectionData { section_label: string; section_display_label?: string | null; section_category?: string; question_count: number; test_id?: number | null }

interface Props { open: boolean; onClose: () => void; onCreated?: () => void }

// Ödev türleri sadeleştirildi: "Kitap Ödevi" ve "Haftalık Tarama Ödevi"
// fonksiyonel olarak Test Ödevi ile aynı akışı kullanıyordu, bu yüzden kaldırıldı.
// Geriye sadece "Test Ödevi" (kitap + test seçimi) ve "Diğer Ödevler" kaldı.
type HwType = 'Test Ödevi' | 'Diğer Ödevler';
type Step = 'type' | 'class' | 'book' | 'konu' | 'test' | 'details' | 'diger' | 'students' | 'verify' | 'done' | 'error';

const CATEGORY_ORDER = ['Kazanım Denemeleri', 'Tekrar Föyü', 'Yazılı Hazırlık Föyü', 'Testler'];

const CloseBtn: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button onClick={onClick}
    className="absolute top-4 right-4 w-[36px] h-[36px] rounded-full flex items-center justify-center hover:bg-gray-200 z-10">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
      <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
    </svg>
  </button>
);
const Sel: React.FC<{ on: boolean }> = ({ on }) => (
  <div className="w-[28px] h-[28px] rounded-full border-2 border-[#e5599b] flex items-center justify-center flex-shrink-0"
    style={{ background: on ? '#e5599b' : '#fafafa' }}>
    {on && <svg width="12" height="9" viewBox="0 0 14 11" fill="none"><path d="M1 5.5L5 9.5L13 1.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" /></svg>}
  </div>
);
const Spinner: React.FC = () => (
  <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#e5599b] border-t-transparent rounded-full animate-spin" /></div>
);

const CreateAssignmentModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState<Step>('type');
  const [hwType, setHwType] = useState<HwType>('Test Ödevi');

  // Shared
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [books, setBooks] = useState<BookData[]>([]);
  const [byCategory, setByCategory] = useState<Record<string, SectionData[]>>({});
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Selections
  // T13 — a homework can target multiple classes (şubes). selectedClasses holds
  // the picked set; `selectedClass` (the first) is kept as the representative
  // for book/kazanım loading + single-class student subset. For a book (Test)
  // assignment all picked classes must share a grade (enforced in the class
  // step + by the backend), so the first class's grade is authoritative.
  const [selectedClasses, setSelectedClasses] = useState<ClassData[]>([]);
  const selectedClass = selectedClasses[0] ?? null;
  const multiClass = selectedClasses.length > 1;
  const toggleClass = (c: ClassData) => setSelectedClasses(prev =>
    prev.some(x => x.id === c.id) ? prev.filter(x => x.id !== c.id) : [...prev, c]
  );
  const [selectedBook, setSelectedBook] = useState<BookData | null>(null);
  const [selectedKonu, setSelectedKonu] = useState<string>('');   // category e.g. "Kazanım Denemeleri"
  const [selectedTest, setSelectedTest] = useState<string>('');   // section_label e.g. "Kazanım Denemesi 5"
  // P3: new-model tests carry a numeric id — pinned into the [BOOK:…|TEST:…]
  // tag so the student runner targets the exact test (labels can collide).
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  // T25: teachers can now pick the deadline hour too. Defaults to 23:59 (TR),
  // preserving the old "end of the day" behaviour when they don't touch it.
  const [dueTime, setDueTime] = useState('23:59');
  const [indefinite, setIndefinite] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [targetAllStudents, setTargetAllStudents] = useState(true);

  // Diğer Ödev specific
  const [digerDesc, setDigerDesc] = useState('');
  const [digerFile, setDigerFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // HW-1: optional metadata a teacher can attach to a Diğer Ödev — all fields
  // are optional. Stored in the assignment description via a [OTHER:...] tag
  // so no backend/schema change is required; consumers that care can parse it
  // out the same way the [BOOK:...] and [FILE:...] tags are parsed.
  const [digerQuestionCount, setDigerQuestionCount] = useState('');
  const [digerAnswers, setDigerAnswers] = useState('');
  const [digerSubject, setDigerSubject] = useState('');
  const [digerUnit, setDigerUnit] = useState('');
  // D6: kazanım picker — options fetched per class (+book/section), selection
  // sent as payload.learningOutcomes. Suggested book outcomes pre-check.
  const [outcomeOpts, setOutcomeOpts] = useState<OutcomeOptions | null>(null);
  const [selMebIds, setSelMebIds] = useState<number[]>([]);
  const [selBookUnitIds, setSelBookUnitIds] = useState<number[]>([]);

  const reset = () => {
    setStep('type'); setHwType('Test Ödevi');
    setSelectedClasses([]); setSelectedBook(null); setByCategory({});
    setSelectedKonu(''); setSelectedTest('');
    setTitle(''); setDueDate(''); setIndefinite(false);
    setSelectedStudentIds([]); setTargetAllStudents(true);
    setApiError(null); setDigerDesc(''); setDigerFile(null);
    setDigerQuestionCount(''); setDigerAnswers(''); setDigerSubject(''); setDigerUnit('');
    setOutcomeOpts(null); setSelMebIds([]); setSelBookUnitIds([]);
  };
  const handleClose = () => { reset(); onClose(); };

  // Load classes on open
  useEffect(() => {
    if (!open) return;
    setLoadingClasses(true);
    teacherApi.classesForAssignment()
      .then(res => setClasses((res.data as ClassData[]) || []))
      .catch(() => setClasses([]))
      .finally(() => setLoadingClasses(false));
  }, [open]);

  // Load books when class selected (for non-diğer flow)
  useEffect(() => {
    if (!selectedClass || hwType === 'Diğer Ödevler') return;
    setLoadingBooks(true); setSelectedBook(null); setBooks([]);
    teacherApi.booksForAssignment(selectedClass.id)
      .then(res => setBooks((res.data as BookData[]) || []))
      .catch(e => { setApiError(e?.response?.data?.error ?? 'Kitaplar yüklenemedi'); setStep('error'); })
      .finally(() => setLoadingBooks(false));
  }, [selectedClass, hwType]);

  // Load book sections (grouped by category) when book selected for Test/Haftalık
  const needsTestPick = hwType === 'Test Ödevi';
  useEffect(() => {
    if (!selectedBook || !needsTestPick) { setByCategory({}); setSelectedKonu(''); setSelectedTest(''); setSelectedTestId(null); return; }
    setLoadingSections(true);
    teacherApi.bookSections(selectedBook.id)
      .then(res => setByCategory(res.data?.byCategory ?? {}))
      .catch(() => setByCategory({}))
      .finally(() => setLoadingSections(false));
  }, [selectedBook, needsTestPick]);

  // D6: kazanım seçici seçenekleri. Diğer Ödev → sınıf seçilince yüklenir;
  // kitap ödevi → kitap (+test) seçilince yüklenir ve bölümle ilişkili kitap
  // kazanımları otomatik ön-işaretlenir (öğretmen değiştirebilir).
  useEffect(() => {
    if (!open || hwType !== 'Diğer Ödevler' || !selectedClass) return;
    teacherApi.assignmentOutcomeOptions({ classId: selectedClass.id })
      .then(res => setOutcomeOpts(res.data as OutcomeOptions))
      .catch(() => setOutcomeOpts(null));
  }, [open, hwType, selectedClass]);

  useEffect(() => {
    if (!open || hwType === 'Diğer Ödevler' || !selectedClass || !selectedBook) return;
    if (needsTestPick && !selectedTest) return;
    teacherApi.assignmentOutcomeOptions({
      classId: selectedClass.id,
      bookId: selectedBook.id,
      sectionLabel: needsTestPick ? selectedTest : undefined,
      testId: needsTestPick ? selectedTestId ?? undefined : undefined,
    })
      .then(res => {
        const data = res.data as OutcomeOptions;
        setOutcomeOpts(data);
        setSelMebIds([]);
        setSelBookUnitIds((data.suggested_book ?? []).map(s => s.id));
      })
      .catch(() => setOutcomeOpts(null));
  }, [open, hwType, selectedClass, selectedBook, selectedTest, needsTestPick]);

  if (!open) return null;

  const wrap = (ttl: string, sub: string, content: React.ReactNode, footer?: React.ReactNode) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={handleClose} />
      <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[860px] max-h-[85vh] flex flex-col" style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
        <CloseBtn onClick={handleClose} />
        <div className="px-7 pt-6 pb-3">
          <h2 style={{ ...F, fontSize: 26, fontWeight: 700, color: '#e5599b', marginBottom: 4 }}>{ttl}</h2>
          <p style={{ ...F, fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>{sub}</p>
        </div>
        <div className="flex-1 overflow-y-auto px-7 pb-3">{content}</div>
        {footer && <div className="px-7 pb-5 pt-2">{footer}</div>}
      </div>
    </div>
  );

  const navBtns = (back: (() => void) | null, forward: () => void, fwdLabel = 'İleri', disabled = false) => (
    <div className="flex gap-3">
      {back && <button onClick={back} className="flex-1 rounded-[12px] py-3 hover:opacity-90" style={{ background: '#3973bf' }}>
        <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Geri</span>
      </button>}
      <button onClick={forward} disabled={disabled}
        className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-40" style={{ background: '#e5599b' }}>
        <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>{fwdLabel}</span>
      </button>
    </div>
  );

  // ── DONE / ERROR ──────────────────────────────────────────────────────────
  if (step === 'done') return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={handleClose} />
      <div className="relative bg-[#f6f6f6] rounded-[12px] w-full max-w-[500px] p-8 text-center">
        <CloseBtn onClick={handleClose} />
        <div className="w-[120px] h-[120px] rounded-full bg-[#e5599b] flex items-center justify-center mx-auto mb-5">
          <svg width="45" height="35" viewBox="0 0 60 45" fill="none"><path d="M5 22L22 39L55 6" stroke="#8c305b" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </div>
        <p style={{ ...F, fontSize: 18, fontWeight: 700, color: '#2b2b2b', marginBottom: 20 }}>Ödeviniz Başarı İle Oluşturuldu</p>
        <button onClick={handleClose} className="rounded-[12px] px-8 py-3 hover:opacity-90" style={{ background: '#e5599b' }}>
          <span style={{ ...F, fontSize: 16, fontWeight: 600, color: '#fafafa' }}>Kapat</span>
        </button>
      </div>
    </div>
  );
  if (step === 'error') return wrap('Hata', apiError ?? 'Bir hata oluştu.',
    <p className="text-[#d40000] text-[14px] py-4">{apiError}</p>,
    <button onClick={() => { setStep('type'); setApiError(null); }} className="w-full rounded-[12px] py-3" style={{ background: '#3973bf' }}>
      <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Geri Dön</span>
    </button>,
  );

  // ── STEP 1: TYPE ──────────────────────────────────────────────────────────
  if (step === 'type') return wrap('Ödev Türü Seçimi', 'Atamak istediğiniz ödevin türünü seçiniz',
    <div className="grid grid-cols-2 gap-3 py-2">
      {(['Test Ödevi', 'Diğer Ödevler'] as HwType[]).map(t => (
        <button key={t}
          onClick={() => { setHwType(t); setStep(t === 'Diğer Ödevler' ? 'diger' : 'class'); }}
          className="bg-[#fafafa] rounded-[16px] px-5 py-5 flex items-center justify-between hover:shadow-md transition-shadow">
          <span style={{ ...F, fontSize: 18, fontWeight: 700, color: '#2b2b2b' }}>{t}</span>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2"><path d="M7 4l6 6-6 6" /></svg>
        </button>
      ))}
    </div>
  );

  // ── STEP 2: CLASS (for non-diğer) ─────────────────────────────────────────
  // T13 — multi-select. A book (Test) assignment must land on one grade (the
  // book is grade-specific), so once a class is picked, other-grade classes are
  // disabled — the teacher can still pick every şube of that grade (9-A, 9-B…).
  if (step === 'class') {
    const lockedGrade = selectedClasses[0]?.grade_number ?? null;
    return wrap('Sınıf Seçimi', 'Ödev atamak istediğiniz sınıf(lar)ı seçin. Birden fazla şube seçebilirsiniz.',
      loadingClasses ? <Spinner /> : classes.length === 0
        ? <p className="text-center text-gray-400 py-10">Atanmış sınıfınız yok.</p>
        : <div className="grid grid-cols-2 gap-3 py-2">
            {classes.map(c => {
              const on = selectedClasses.some(x => x.id === c.id);
              const disabled = !on && lockedGrade != null && (c.grade_number ?? null) !== lockedGrade;
              return (
                <button key={c.id} onClick={() => !disabled && toggleClass(c)} disabled={disabled}
                  className={`rounded-[16px] px-5 py-4 flex items-center justify-between transition-shadow text-left bg-[#fafafa] border-2 ${on ? 'border-[#e5599b]' : 'border-transparent'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:shadow-md'}`}>
                  <div>
                    <p style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{c.name}</p>
                    <p style={{ ...F, fontSize: 13, color: '#888' }}>
                      {c.students.length} öğrenci{disabled ? ' · farklı seviye' : ''}
                    </p>
                  </div>
                  <Sel on={on} />
                </button>
              );
            })}
          </div>,
      navBtns(() => setStep('type'), () => { if (selectedClasses.length) setStep('students'); }, 'İleri', selectedClasses.length === 0),
    );
  }

  // ── STEP 3: BOOK ──────────────────────────────────────────────────────────
  if (step === 'book') return wrap(hwType, `${selectedClass?.name} için kitap seçin.`,
    loadingBooks ? <Spinner /> : books.length === 0
      ? <p className="text-center text-gray-400 py-10">Uygun kitap bulunamadı.</p>
      : <div className="grid grid-cols-2 gap-3 py-2">
          {books.map(b => (
            <button key={b.id} onClick={() => setSelectedBook(b)}
              className={`rounded-[14px] p-3 flex gap-3 text-left border-2 transition-all hover:shadow-md bg-[#fafafa] ${selectedBook?.id === b.id ? 'border-[#e5599b]' : 'border-transparent'}`}>
              {/* AP56: backend artık çözülmüş cover_url döndürüyor — ham S3 key
                  (cover_image_url) tarayıcıda yüklenemiyordu. */}
              <img src={b.cover_url || b.cover_image_url || '/a-book1.png'} alt="" className="w-[70px] h-[96px] rounded-[8px] object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }} className="line-clamp-3">{b.title}</p>
                {b.subject && <p style={{ ...F, fontSize: 12, color: '#3973bf' }}>{b.subject.name}</p>}
                {b.grade_levels && <p style={{ ...F, fontSize: 12, color: '#888' }}>{b.grade_levels.name}</p>}
              </div>
            </button>
          ))}
        </div>,
    navBtns(() => setStep('students'), () => {
      if (!selectedBook) return;
      // Kitap Ödevi skips konu/test steps, goes directly to details
      setStep(needsTestPick ? 'konu' : 'details');
    }, 'İleri', !selectedBook),
  );

  // ── STEP 4: KONU (Test/Haftalık only) ─────────────────────────────────────
  if (step === 'konu') {
    const categories = Object.keys(byCategory).sort((a, b) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      if (ia >= 0 && ib >= 0) return ia - ib;
      if (ia >= 0) return -1;
      if (ib >= 0) return 1;
      return a.localeCompare(b);
    });
    return wrap(hwType, 'Ödev vermek istediğiniz konu/kategori seçin.',
      loadingSections ? <Spinner /> : categories.length === 0
        ? <p className="text-center text-gray-400 py-10">Bu kitap için konu bulunamadı.</p>
        : <div className="grid grid-cols-2 gap-3 py-2">
            {categories.map(cat => (
              <button key={cat} onClick={() => { setSelectedKonu(cat); setSelectedTest(''); setStep('test'); }}
                className={`bg-[#fafafa] rounded-[16px] px-5 py-4 flex items-center justify-between hover:shadow-md transition-shadow text-left border-2 ${selectedKonu === cat ? 'border-[#e5599b]' : 'border-transparent'}`}>
                <div>
                  <p style={{ ...F, fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{cat}</p>
                  <p style={{ ...F, fontSize: 12, color: '#888' }}>{byCategory[cat].length} test</p>
                </div>
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#2b2b2b" strokeWidth="2"><path d="M7 4l6 6-6 6" /></svg>
              </button>
            ))}
          </div>,
      navBtns(() => setStep('book'), () => { if (selectedKonu) setStep('test'); }, 'İleri', !selectedKonu),
    );
  }

  // ── STEP 5: TEST (Test/Haftalık only) ─────────────────────────────────────
  if (step === 'test') {
    const tests = byCategory[selectedKonu] ?? [];
    return wrap(hwType, `${selectedKonu} — hangi testi atıyorsunuz?`,
      tests.length === 0
        ? <p className="text-center text-gray-400 py-10">Test bulunamadı.</p>
        : <div className="grid grid-cols-3 gap-2 py-2">
            {tests.map(t => (
              <button key={t.test_id ?? t.section_label} onClick={() => { setSelectedTest(t.section_label); setSelectedTestId(t.test_id ?? null); }}
                className={`bg-[#fafafa] rounded-[14px] px-4 py-3 text-left hover:shadow-md transition-shadow border-2 ${(t.test_id != null ? selectedTestId === t.test_id : selectedTest === t.section_label) ? 'border-[#e5599b]' : 'border-transparent'}`}>
                <p style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>{t.section_display_label || t.section_label}</p>
                <p style={{ ...F, fontSize: 12, color: '#888' }}>{t.question_count} soru</p>
              </button>
            ))}
          </div>,
      navBtns(() => setStep('konu'), () => { if (selectedTest) setStep('details'); }, 'İleri', !selectedTest),
    );
  }

  // ── STEP 6: DETAILS (title + due date) ─────────────────────────────────────
  if (step === 'details') return wrap(hwType, 'Başlık girin ve son tarihi seçin.',
    <div className="space-y-4 py-2">
      <div>
        <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Ödev Başlığı *</label>
        <input value={title} onChange={e => setTitle(e.target.value)}
          placeholder={needsTestPick ? `${selectedTest} Ödevi` : 'Örn: Kitap Okuma Ödevi'}
          className="w-full bg-[#fafafa] rounded-[14px] px-4 py-3 outline-none text-[15px]"
          style={{ border: `2px solid ${title.trim() ? '#3973bf' : '#e5599b'}`, ...F }} />
      </div>
      <div>
        <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Son Tarih (opsiyonel)</label>
        <div className="flex gap-2">
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} disabled={indefinite}
            className="flex-1 bg-[#fafafa] rounded-[14px] px-4 py-3 outline-none text-[15px]"
            style={{ border: '2px solid #3973bf', ...F, opacity: indefinite ? 0.4 : 1 }} />
          <input type="time" value={dueTime} onChange={e => setDueTime(e.target.value)} disabled={indefinite}
            aria-label="Son saat"
            className="w-[120px] bg-[#fafafa] rounded-[14px] px-4 py-3 outline-none text-[15px]"
            style={{ border: '2px solid #3973bf', ...F, opacity: indefinite ? 0.4 : 1 }} />
        </div>
        <button onClick={() => setIndefinite(!indefinite)} className="flex items-center gap-2 mt-2">
          <Sel on={indefinite} />
          <span style={{ ...F, fontSize: 13, color: '#2b2b2b' }}>Süresiz</span>
        </button>
      </div>
      <div>
        <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>MEB Kazanımları / Kitap Konuları (opsiyonel)</label>
        {outcomeOpts ? (
          <OutcomePicker
            options={outcomeOpts}
            mebIds={selMebIds}
            bookUnitIds={selBookUnitIds}
            onChange={sel => { setSelMebIds(sel.mebIds); setSelBookUnitIds(sel.bookUnitIds); }}
          />
        ) : (
          <p style={{ ...F, fontSize: 12, color: '#888' }}>MEB kazanımları ve kitap konuları yükleniyor…</p>
        )}
      </div>
      <div className="bg-white rounded-[12px] p-4 border border-gray-100 mt-4">
        <p style={{ ...F, fontSize: 13, color: '#888', marginBottom: 4 }}>Özet</p>
        <p style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }}>
          {hwType} — {selectedClass?.name} — {selectedBook?.title}
          {needsTestPick && ` — ${selectedTest}`}
          {(selMebIds.length + selBookUnitIds.length) > 0 &&
            ` — ${selMebIds.length} MEB kazanımı, ${selBookUnitIds.length} kitap konusu`}
        </p>
      </div>
    </div>,
    navBtns(() => setStep('test'), () => { if (title.trim()) setStep('verify'); }, 'İleri', !title.trim()),
  );

  // ── DİĞER ─────────────────────────────────────────────────────────────────
  if (step === 'diger') return wrap('Diğer Ödev', 'Ödevinizin detaylarını giriniz.',
    <div className="space-y-4 py-2">
      <div>
        <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Ödev Başlığı *</label>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Örn: Araştırma Ödevi"
          className="w-full bg-[#fafafa] rounded-[14px] px-4 py-3 outline-none text-[15px]"
          style={{ border: `2px solid ${title.trim() ? '#3973bf' : '#e5599b'}`, ...F }} />
      </div>
      <div>
        <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Sınıf(lar) *</label>
        {classes.length === 0 ? (
          <p style={{ ...F, fontSize: 13, color: '#888' }}>Atanmış sınıfınız yok.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {classes.map(c => {
              const on = selectedClasses.some(x => x.id === c.id);
              return (
                <button key={c.id} type="button" onClick={() => toggleClass(c)}
                  className="rounded-[12px] px-3 py-2 flex items-center gap-2 border-2"
                  style={{ ...F, fontSize: 14, fontWeight: 600, borderColor: on ? '#e5599b' : '#d8d8d8', background: on ? '#fdeef5' : '#fafafa', color: '#2b2b2b' }}>
                  <Sel on={on} />
                  {c.name}
                </button>
              );
            })}
          </div>
        )}
        <p style={{ ...F, fontSize: 12, color: '#888', marginTop: 6 }}>Birden fazla sınıf seçebilirsiniz.</p>
      </div>
      {/* T26: Diğer Ödev now supports a son tarih too (was test-only). Backend
          snaps a date-only pick to 23:59 TR via schoolEndOfDay. */}
      <div>
        <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Son Tarih (opsiyonel)</label>
        <div className="flex gap-2">
          <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} disabled={indefinite}
            className="flex-1 bg-[#fafafa] rounded-[14px] px-4 py-3 outline-none text-[15px]"
            style={{ border: '2px solid #3973bf', ...F, opacity: indefinite ? 0.4 : 1 }} />
          <input type="time" value={dueTime} onChange={e => setDueTime(e.target.value)} disabled={indefinite}
            aria-label="Son saat"
            className="w-[120px] bg-[#fafafa] rounded-[14px] px-4 py-3 outline-none text-[15px]"
            style={{ border: '2px solid #3973bf', ...F, opacity: indefinite ? 0.4 : 1 }} />
        </div>
        <button onClick={() => setIndefinite(!indefinite)} className="flex items-center gap-2 mt-2">
          <Sel on={indefinite} />
          <span style={{ ...F, fontSize: 13, color: '#2b2b2b' }}>Süresiz</span>
        </button>
      </div>
      <div>
        <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Açıklama</label>
        <textarea value={digerDesc} onChange={e => setDigerDesc(e.target.value)} rows={4}
          placeholder="Ödevin detaylarını açıklayın…"
          className="w-full bg-[#fafafa] rounded-[14px] px-4 py-3 outline-none text-[15px] resize-none"
          style={{ border: '2px solid #3973bf', ...F }} />
      </div>
      <div>
        <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Dosya Ekle (opsiyonel)</label>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg" className="hidden"
          onChange={e => setDigerFile(e.target.files?.[0] ?? null)} />
        <button onClick={() => fileRef.current?.click()}
          className="w-full bg-[#fafafa] rounded-[14px] px-4 py-4 flex items-center gap-3 hover:bg-gray-50"
          style={{ border: '2px dashed #3973bf' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3973bf" strokeWidth="2" strokeLinecap="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
          </svg>
          <span style={{ ...F, fontSize: 14, fontWeight: 500, color: '#3973bf' }}>
            {digerFile ? digerFile.name : 'PDF, Word veya Görsel yükle'}
          </span>
        </button>
        {digerFile && <button onClick={() => setDigerFile(null)} className="text-[12px] text-red-400 mt-1 hover:underline">Dosyayı kaldır</button>}
      </div>

      {/* Cevap Anahtarı — its own block so it's discoverable. Released to
          students later via "Cevapları Yayınla" (Cevapları Görüntüle akışı). */}
      <div className="bg-white rounded-[12px] p-4" style={{ border: '2px solid #3973bf' }}>
        <label style={{ ...F, fontSize: 14, fontWeight: 700, color: '#3973bf', display: 'block', marginBottom: 6 }}>
          Cevap Anahtarı (opsiyonel)
        </label>
        <textarea
          value={digerAnswers}
          onChange={e => setDigerAnswers(e.target.value)}
          placeholder="Örn: 1a, 2b, 3d   ·   veya   1: ..., 2: ..."
          rows={3}
          className="w-full bg-[#fafafa] rounded-[12px] px-4 py-3 outline-none text-[15px] resize-none"
          style={{ border: '1px solid #c5c5c5', ...F }}
        />
        <p style={{ ...F, fontSize: 12, color: '#888', marginTop: 6 }}>
          Ödevi tamamlayıp <strong>“Cevapları Yayınla”</strong> dediğinizde öğrenciler bu cevapları görüp kendi çözümleriyle karşılaştırabilir.
        </p>
      </div>

      {/* HW-1: optional metadata next to the image/file upload. All fields are
          optional; left blank ones don't get added to the assignment. */}
      <div className="bg-white rounded-[12px] p-4 border border-gray-200">
        <p style={{ ...F, fontSize: 13, fontWeight: 700, color: '#3973bf', marginBottom: 10 }}>
          Diğer Soru Alanı (opsiyonel)
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 4 }}>Soru Sayısı</label>
            <input
              type="number"
              min={0}
              value={digerQuestionCount}
              onChange={e => setDigerQuestionCount(e.target.value)}
              placeholder="Örn: 20"
              className="w-full bg-[#fafafa] rounded-[10px] px-3 py-2 outline-none text-[14px]"
              style={{ border: '1px solid #c5c5c5', ...F }}
            />
          </div>
          <div>
            <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 4 }}>Ders</label>
            <input
              value={digerSubject}
              onChange={e => setDigerSubject(e.target.value)}
              placeholder="Örn: Matematik"
              className="w-full bg-[#fafafa] rounded-[10px] px-3 py-2 outline-none text-[14px]"
              style={{ border: '1px solid #c5c5c5', ...F }}
            />
          </div>
          <div>
            <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 4 }}>Ünite</label>
            <input
              value={digerUnit}
              onChange={e => setDigerUnit(e.target.value)}
              placeholder="Örn: Türev"
              className="w-full bg-[#fafafa] rounded-[10px] px-3 py-2 outline-none text-[14px]"
              style={{ border: '1px solid #c5c5c5', ...F }}
            />
          </div>
        </div>
      </div>

      <div>
        <label style={{ ...F, fontSize: 12, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 4 }}>MEB Kazanımları / Kitap Konuları (opsiyonel)</label>
        {outcomeOpts ? (
          <OutcomePicker
            options={outcomeOpts}
            mebIds={selMebIds}
            bookUnitIds={selBookUnitIds}
            onChange={sel => { setSelMebIds(sel.mebIds); setSelBookUnitIds(sel.bookUnitIds); }}
          />
        ) : (
          <p style={{ ...F, fontSize: 12, color: '#888' }}>
            {selectedClass ? 'MEB kazanımları ve kitap konuları yükleniyor…' : 'Etiket seçmek için önce sınıf seçin.'}
          </p>
        )}
      </div>

      <p style={{ ...F, fontSize: 12, color: '#888' }}>⚠️ Diğer Ödevler öğrenci performansını etkilemez.</p>
    </div>,
    navBtns(() => setStep('type'), () => { if (title.trim() && selectedClass) setStep('students'); }, 'İleri', !title.trim() || !selectedClass),
  );

  // ── STUDENTS ──────────────────────────────────────────────────────────────
  if (step === 'students') {
    const classStudents = selectedClass?.students ?? [];
    const allSel = selectedStudentIds.length === classStudents.length;
    const toggle = (id: string) => setSelectedStudentIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    // T13 — per-student subset only makes sense for a single class. When more
    // than one class (şube) is picked, the homework always goes to every
    // student in every selected class.
    const totalStudents = selectedClasses.reduce((n, c) => n + c.students.length, 0);
    if (multiClass) return wrap('Öğrenci Seçimi', 'Seçtiğiniz sınıfların tüm öğrencilerine atanacak.',
      <div className="space-y-3 py-2">
        <div className="rounded-[12px] p-4 bg-[#fdeef5]" style={{ border: '2px solid #e5599b' }}>
          <p style={{ ...F, fontSize: 14, fontWeight: 700, color: '#2b2b2b', marginBottom: 6 }}>
            {selectedClasses.length} sınıf · {totalStudents} öğrenci
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedClasses.map(c => (
              <span key={c.id} className="rounded-[8px] px-3 py-1" style={{ ...F, fontSize: 13, fontWeight: 600, background: '#fff', color: '#e5599b', border: '1px solid #f3c3da' }}>
                {c.name} ({c.students.length})
              </span>
            ))}
          </div>
        </div>
        <p style={{ ...F, fontSize: 13, color: '#888' }}>
          Belirli öğrenci seçimi yalnızca tek sınıf seçildiğinde yapılabilir.
        </p>
      </div>,
      navBtns(
        () => setStep(hwType === 'Diğer Ödevler' ? 'diger' : 'class'),
        () => setStep(hwType === 'Diğer Ödevler' ? 'verify' : 'book'),
        'İleri',
      ),
    );
    return wrap('Öğrenci Seçimi', 'Tüm sınıfı seçebilir, belirli öğrencileri ya da tek bir öğrenciyi hedefleyebilirsiniz.',
      <div className="space-y-4 py-2">
        <div className="flex gap-3">
          {[true, false].map(all => (
            <button key={String(all)} onClick={() => { setTargetAllStudents(all); if (all) setSelectedStudentIds([]); }}
              className="flex-1 py-3 rounded-[12px] font-semibold text-[14px]"
              style={{ background: targetAllStudents === all ? '#e5599b' : '#e5e5e5', color: targetAllStudents === all ? '#fff' : '#2b2b2b', ...F }}>
              {all ? `Tüm Sınıf (${classStudents.length})` : 'Belirli Öğrenci(ler)'}
            </button>
          ))}
        </div>
        {!targetAllStudents && (
          <>
            {/* HW-2: teacher can assign to a single student — show count so
                the one-student case is obvious. */}
            <div className="flex items-center justify-between">
              <span style={{ ...F, fontSize: 13, color: '#2b2b2b' }}>
                {selectedStudentIds.length === 0
                  ? 'En az bir öğrenci seçin.'
                  : selectedStudentIds.length === 1
                  ? 'Ödev yalnızca seçilen öğrenciye atanacak.'
                  : `${selectedStudentIds.length} öğrenci seçildi.`}
              </span>
              <button onClick={() => setSelectedStudentIds(allSel ? [] : classStudents.map(s => s.id))}
                className="text-[13px] font-semibold rounded-[8px] px-4 py-2"
                style={{ background: allSel ? '#e5599b' : '#3973bf', color: '#fff', ...F }}>
                {allSel ? 'Seçimi Kaldır' : 'Tümünü Seç'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {classStudents.map(s => (
                <button key={s.id} onClick={() => toggle(s.id)}
                  className="bg-[#fafafa] rounded-[12px] px-4 py-3 flex items-center gap-3 hover:shadow-sm">
                  <div className="w-[34px] h-[34px] rounded-full bg-[#3973bf] overflow-hidden flex-shrink-0">
                    <img src="/a-avatar.png" alt="" className="w-full h-full object-cover" />
                  </div>
                  <span className="flex-1 text-left" style={{ ...F, fontSize: 14, fontWeight: 500, color: '#2b2b2b' }}>{s.name}</span>
                  <Sel on={selectedStudentIds.includes(s.id)} />
                </button>
              ))}
            </div>
          </>
        )}
      </div>,
      navBtns(
        () => setStep(hwType === 'Diğer Ödevler' ? 'diger' : 'class'),
        () => {
          if (!targetAllStudents && selectedStudentIds.length === 0) return;
          // Diğer Ödev has no book/test — jump to verify. Others pick book next.
          setStep(hwType === 'Diğer Ödevler' ? 'verify' : 'book');
        },
        'İleri',
        !targetAllStudents && selectedStudentIds.length === 0,
      ),
    );
  }

  // ── VERIFY + SUBMIT ───────────────────────────────────────────────────────
  if (step === 'verify') {
    // Multi-class → always all students; single class keeps the subset choice.
    const allStudents = multiClass || targetAllStudents;
    const classNames = selectedClasses.map(c => c.name).join(', ');
    const targetDesc = allStudents ? `${classNames} — Tüm öğrenciler` : `${selectedClass?.name} — ${selectedStudentIds.length} öğrenci`;

    const handleSubmit = async () => {
      setSubmitting(true); setApiError(null);
      try {
        const payload: any = {
          title: title.trim(),
          academicYearId: 1,
          // T25: send a NAIVE "YYYY-MM-DDTHH:mm" (school-clock TR) — never
          // toISOString(), which would reinterpret it in the browser's tz. The
          // backend (resolveSchoolDeadline) reads the digits as Türkiye time.
          dueDate: (!indefinite && dueDate) ? `${dueDate}T${dueTime || '23:59'}` : undefined,
          classTargets: allStudents ? selectedClasses.map(c => c.id) : undefined,
          studentTargets: !allStudents ? selectedStudentIds : undefined,
        };

        // D6: picker shown → the selection is authoritative (empty = none).
        // When the picker never loaded, omit so the backend can auto-attach
        // the section's tagged outcomes on book homework.
        if (outcomeOpts) {
          payload.learningOutcomes = { mebIds: selMebIds, bookUnitIds: selBookUnitIds };
        }

        if (hwType === 'Diğer Ödevler') {
          payload.assignmentType = 'other';
          payload.subjectId = 0;

          // HW-1: serialize the optional metadata into the description with a
          // [OTHER:...] tag so backend parsers (or future schema additions)
          // can pick up questionCount/subject/unit/outcome/answers. Pipes are
          // escaped as \| to keep the delimiter unambiguous if the user types
          // one into a text field.
          const esc = (v: string) => v.trim().replace(/\|/g, '\\|');
          const otherFields: string[] = [];
          if (digerQuestionCount.trim()) otherFields.push(`questionCount=${esc(digerQuestionCount)}`);
          if (digerSubject.trim()) otherFields.push(`subject=${esc(digerSubject)}`);
          if (digerUnit.trim()) otherFields.push(`unit=${esc(digerUnit)}`);
          if (digerAnswers.trim()) otherFields.push(`answers=${esc(digerAnswers)}`);
          const otherTag = otherFields.length > 0 ? `[OTHER:${otherFields.join('|')}]\n` : '';
          payload.description = `${otherTag}${digerDesc || ''}`.trim() || undefined;

          if (digerFile) {
            // Stream the file through the backend to S3. Unlike the old
            // presigned-PUT path this doesn't depend on bucket CORS, and we
            // surface a failure instead of silently creating a fileless ödev.
            const { data: up } = await teacherApi.uploadHomeworkFile(digerFile);
            payload.description = `[FILE:${up.key}]\n${otherTag}${digerDesc || ''}`.trim();
          }
        } else {
          payload.bookId = selectedBook!.id;
          payload.subjectId = selectedBook!.subject?.id ?? 0;
          payload.assignmentType = 'test_assignment';
          const sectionLabel = needsTestPick ? selectedTest : '';
          const testTag = needsTestPick && selectedTestId != null ? `|TEST:${selectedTestId}` : '';
          payload.description = `[BOOK:${selectedBook!.id}|SECTION:${sectionLabel}${testTag}]\n${hwType}${sectionLabel ? ' — ' + sectionLabel : ''}`;
        }

        await teacherApi.createAssignment(payload);
        setStep('done');
        // Tell the parent list to refetch so the new homework shows up without
        // a manual page reload.
        onCreated?.();
      } catch (e: any) {
        setApiError(e?.response?.data?.error ?? e.message ?? 'Ödev oluşturulamadı');
        setStep('error');
      } finally {
        setSubmitting(false);
      }
    };

    const selectedStudentNames = !allStudents
      ? (selectedClass?.students.filter(s => selectedStudentIds.includes(s.id)).map(s => s.name) ?? [])
      : [];

    return wrap('Özet', 'Ödev detaylarını kontrol edin.',
      <div className="bg-[#fafafa] rounded-[12px] p-5 space-y-3">
        {[
          { l: 'Ödev Türü:', v: hwType },
          { l: 'Başlık:', v: title },
          ...(hwType !== 'Diğer Ödevler' ? [{ l: 'Kitap:', v: selectedBook?.title ?? '—' }] : []),
          ...(needsTestPick ? [{ l: 'Konu:', v: selectedKonu }, { l: 'Test:', v: selectedTest }] : []),
          { l: selectedClasses.length > 1 ? 'Sınıflar:' : 'Sınıf:', v: classNames || '—' },
          { l: 'Hedef:', v: targetDesc },
          { l: 'Son Tarih:', v: indefinite ? 'Süresiz' : (dueDate ? `${dueDate.split('-').reverse().join('.')} ${dueTime || '23:59'}` : 'Belirtilmedi') },
          ...(hwType === 'Diğer Ödevler' && digerFile ? [{ l: 'Dosya:', v: digerFile.name }] : []),
        ].map(r => (
          <div key={r.l} className="flex gap-3 flex-wrap">
            <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#3973bf', minWidth: 120 }}>{r.l}</span>
            <span style={{ ...F, fontSize: 15, fontWeight: 500, color: '#2b2b2b' }}>{r.v}</span>
          </div>
        ))}
        {!allStudents && selectedStudentNames.length > 0 && (
          <div className="pt-2 border-t border-gray-200">
            <p style={{ ...F, fontSize: 13, fontWeight: 600, color: '#3973bf', marginBottom: 6 }}>Seçilen öğrenciler:</p>
            <div className="flex flex-wrap gap-2">
              {selectedStudentNames.map(n => (
                <span key={n} className="bg-white rounded-[8px] px-3 py-1 text-[13px]" style={{ ...F, color: '#2b2b2b', border: '1px solid #e5e5e5' }}>{n}</span>
              ))}
            </div>
          </div>
        )}
        {hwType === 'Diğer Ödevler' && (
          <p style={{ ...F, fontSize: 12, color: '#888' }}>⚠️ Bu ödev performans grafiğini etkilemez.</p>
        )}
      </div>,
      <div className="flex gap-3">
        <button onClick={() => setStep(hwType === 'Diğer Ödevler' ? 'students' : 'details')} className="flex-1 rounded-[12px] py-3 hover:opacity-90" style={{ background: '#3973bf' }}>
          <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Geri</span>
        </button>
        <button onClick={handleSubmit} disabled={submitting} className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50" style={{ background: '#e5599b' }}>
          <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>{submitting ? 'Oluşturuluyor…' : 'Ödevi Oluştur'}</span>
        </button>
      </div>,
    );
  }

  return null;
};

export default CreateAssignmentModal;
